import {
    ChatSession,
    ConversationalChatVisibility,
    LoginStatus,
    Platform
} from '@klant/conversational-connector/klant-conversational-connector.model';
import { ConversationService } from '@klant/conversational-connector/services/conversation/conversation.service';
import { ChatNotificationsModel } from '@klant/conversational-connector/models/ChatNotifications.model';
import { ConversationalConnectorAnalyticsService } from '@klant/conversational-connector/services/analytics/analytics.service';
import { ConversationalConnectorService } from '@klant/conversational-connector/conversational-connector.service';
import { KlantConversationalConnectorService } from '@klant/conversational-connector/klant-conversational-connector.service';

import { Component, computed, DestroyRef, ElementRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';

import { ChannelName } from '@ap/mijn-kanaalconfiguratie';
import { ApContentService } from '@ap/content';
import { ApError } from '@ap/error';

import { ChatSessionStore } from '../../signal-stores/chat-sesion-store/chat-session.store';
import { SessionStorageService } from '../../session-storage/session-storage.service';
import { OperationalCheckService } from '../../shared/services/operational-check.service';
import { ConversationalChatStore } from '../../signal-stores/conversational-chat-store/conversational-chat.store';

import content from './mijn-klant-conversational.content.json';
import { MIJN_CONVERSATIONAL_OPERATIONAL } from './mijn-klant-conversational.constants';

@Component({
    selector: 'mijn-klant-conversational',
    templateUrl: 'mijn-klant-conversational.component.html',
    standalone: false
})
export class MijnKlantConversationalComponent implements OnInit {
    private readonly chatSessionStore = inject(ChatSessionStore);
    private readonly chatStore = inject(ConversationalChatStore);
    private readonly conversationService = inject(ConversationService);
    private readonly apContentService = inject(ApContentService);
    private readonly conversationalConnectorAnalyticsService = inject(ConversationalConnectorAnalyticsService);
    private readonly conversationalConnectorService = inject(ConversationalConnectorService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly elementRef = inject(ElementRef);
    private readonly klantConversationalConnectorService = inject(KlantConversationalConnectorService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly operationalCheckService = inject(OperationalCheckService);

    public takeoverError = signal<ApError>(null);
    public showError = computed(() => this.chatSessionStore.errorGettingChatSessionIdChat() || this.isOperational() === false);
    public showConnector = computed(() => !this.showError() && this.isOperational() === true);
    public unreadMessageCount = signal<number>(null);
    public isOperational = this.operationalCheckService.isOperational;

    @HostListener('document:chatMessage', ['$event'])
    public handleChatMessage(event) {
        this.klantConversationalConnectorService.setFaqMessage(event.detail.chatMessage);
    }

    private chatVisibility$ = toObservable(this.conversationService.getChatVisibility);
    private isOperational$ = toObservable(this.isOperational);
    private unreadMessageCount$ = toObservable(this.unreadMessageCount);
    private chatSession$ = toObservable(this.chatSessionStore.chatSession);

    public ngOnInit(): void {
        this.apContentService.addContentItems(content);
        this.setStaticVariables();
        this.chatStore.setPlatform(Platform.MIJN);

        this.checkIsOperational();
        this.checkOpenChatQueryParam();

        this.observeShouldChatSessionIdBeFetched();
        this.observeShouldResetUnreadMessageCounter();
        this.observeShouldUnreadMessagesBeFetched();
        this.observeShouldChatBubblePopUp();
    }

    private setStaticVariables(): void {
        this.takeoverError.set(
            new ApError(this.apContentService.get('mijn.klant.conversational.takeover.error.message'), undefined, undefined, false)
        );
    }

    private checkOpenChatQueryParam(): void {
        const openChatParam = this.sessionStorageService.getOpenChatQueryParamValue();

        if (openChatParam) {
            this.conversationService.setConversationVisibility(ConversationalChatVisibility.OPEN);
        }
    }

    private checkIsOperational(): void {
        this.operationalCheckService
            .checkIsOperational({
                channelName: ChannelName.MIJN_KLANT,
                configKey: MIJN_CONVERSATIONAL_OPERATIONAL
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    if (this.isOperational() === false) {
                        this.triggerErrorOnMijnContactPage();
                    }
                })
            )
            .subscribe();
    }

    private triggerErrorOnMijnContactPage(): void {
        const message = this.apContentService.get('mijn.klant.conversational.error.message');
        const status = 'error';

        const notificationEvent = new CustomEvent('chatNotification', {
            detail: { message, status }
        });
        this.elementRef.nativeElement.getRootNode().querySelector('mijn-klant-conversational-web').dispatchEvent(notificationEvent);
    }

    private observeShouldChatSessionIdBeFetched(): void {
        combineLatest([this.chatVisibility$, this.isOperational$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([chatVisibility, isOperational]) => {
                if (this.shouldChatSessionIdBeFetched(chatVisibility, isOperational)) {
                    this.fetchChatSessionId();
                }
            });
    }

    private shouldChatSessionIdBeFetched(chatVisibility: ConversationalChatVisibility, isOperational: boolean): boolean {
        return chatVisibility === ConversationalChatVisibility.OPEN && isOperational && !this.chatSessionStore.chatSession();
    }

    private fetchChatSessionId(): void {
        this.conversationalConnectorAnalyticsService.pushAnalyticsPageview(LoginStatus.LOGGED_IN);
        this.chatSessionStore.getChatSessionId();
    }

    private observeShouldResetUnreadMessageCounter(): void {
        this.chatSession$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((chatSession: ChatSession) => {
            if (chatSession) {
                this.chatSessionStore.resetUnreadMessageCounter();
            }
        });
    }

    private observeShouldUnreadMessagesBeFetched(): void {
        combineLatest([this.chatVisibility$, this.isOperational$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([chatVisibility, isOperational]) => {
                if (this.shouldUnreadMessagesBeFetched(chatVisibility, isOperational)) {
                    this.fetchUnreadMessages();
                }
            });
    }

    private shouldUnreadMessagesBeFetched(chatVisibility: ConversationalChatVisibility, isOperational: boolean): boolean {
        return chatVisibility === ConversationalChatVisibility.INVISIBLE && isOperational;
    }

    private fetchUnreadMessages(): void {
        this.conversationalConnectorService
            .getChatNotifications()
            .pipe(
                map((chatNotifications: ChatNotificationsModel) => this.calculateUnreadMessageCount(chatNotifications)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (unreadMessageCount: number) => {
                    this.unreadMessageCount.set(unreadMessageCount);
                }
            });
    }

    private calculateUnreadMessageCount(chatNotifications: ChatNotificationsModel): number {
        return chatNotifications.chatNotifications.reduce((sum, notification) => sum + notification.count, 0);
    }

    private observeShouldChatBubblePopUp(): void {
        this.unreadMessageCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((unreadMessages: number) => {
            const hasUnreadMessages = unreadMessages > 0;

            if (hasUnreadMessages) {
                this.popUpChatBubble(unreadMessages);
            }
        });
    }

    private popUpChatBubble(notificationNumber: number): void {
        this.conversationService.setConversationVisibility(ConversationalChatVisibility.MINIMIZED);
        this.conversationService.updateUnreadMessageCount(notificationNumber);
    }
}
